import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { uuid, vals } from "@/lib/format";
import { BigButton, Field, inputCls } from "@/components/ui-kit";
import VehiclesEditor from "@/components/customers/VehiclesEditor";

const norm = (s) => (s || "").trim().toLowerCase();
const digits = (s) => (s || "").replace(/\D/g, "");

export default function CustomerFormDialog({ open, onOpenChange, customer, onSaved }) {
  const { t, view, dispatch, money } = useApp();
  const [f, setF] = useState({});
  const [confirmDup, setConfirmDup] = useState(false);
  useEffect(() => {
    if (open) setF(customer ? { ...customer } : { name: "", family: "", phone: "", credit_limit: "", notes: "" });
    setConfirmDup(false);
  }, [open, customer]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const auto = view.shop?.type === "autoparts";
  const makes = useMemo(() => (auto ? [...new Set(vals(view.products).map((p) => p.vehicle_make).filter((m) => m && m.toLowerCase() !== "universal"))].sort() : []), [auto, view.products]);

  const dups = useMemo(() => {
    if (customer || !f.name) return [];
    return vals(view.customers).filter((c) => (digits(f.phone) && digits(c.phone) === digits(f.phone))
      || (norm(c.name) === norm(f.name) && norm(c.family) === norm(f.family)));
  }, [f.name, f.family, f.phone, view.customers, customer]);

  const save = () => {
    if (!f.name?.trim()) return;
    if (dups.length && !confirmDup) { setConfirmDup(true); return; }
    const data = { id: customer?.id || uuid(), name: f.name.trim(), family: f.family?.trim() || "", phone: f.phone?.trim() || "", credit_limit: Number(f.credit_limit || 0), notes: f.notes || "", ...(auto ? { vehicles: (f.vehicles || []).filter((v) => v.plate || v.make) } : {}) };
    if (!dispatch("customer.upsert", data)) return;
    toast.success(t("saved"));
    onSaved?.(data);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="customer-form-dialog">
        <DialogTitle className="font-heading text-2xl">{customer ? t("edit_customer") : t("add_customer")}</DialogTitle>
        <div className="space-y-3">
          <Field label={t("name")}><input value={f.name || ""} onChange={set("name")} className={inputCls} data-testid="customer-name-input" /></Field>
          <Field label={t("family")}><input value={f.family || ""} onChange={set("family")} className={inputCls} data-testid="customer-family-input" /></Field>
          <Field label={t("phone_whatsapp")}><input dir="ltr" inputMode="tel" placeholder="+213..." value={f.phone || ""} onChange={set("phone")} className={inputCls} data-testid="customer-phone-input" /></Field>
          <Field label={t("credit_limit")}><input dir="ltr" inputMode="decimal" value={f.credit_limit ?? ""} onChange={set("credit_limit")} className={inputCls} data-testid="customer-limit-input" /></Field>
          <Field label={t("notes")}><input value={f.notes || ""} onChange={set("notes")} className={inputCls} data-testid="customer-notes-input" /></Field>
          {auto && <VehiclesEditor value={f.vehicles || []} onChange={(v) => setF({ ...f, vehicles: v })} makes={makes} />}
        </div>
        {dups.length > 0 && (
          <div className="rounded-xl bg-[#E67E22]/10 border border-[#E67E22]/40 p-3" data-testid="customer-duplicate-warning">
            <div className="font-bold flex items-center gap-1.5 text-[#A85A12]"><AlertTriangle className="w-4 h-4" />{t("possible_duplicate")}</div>
            {dups.map((d) => (
              <button key={d.id} type="button" onClick={() => { onSaved?.(d); onOpenChange(false); }} data-testid={`customer-use-existing-${d.id}`}
                className="mt-2 w-full h-11 px-3 rounded-lg bg-white border border-border flex justify-between items-center font-bold">
                <span>{d.name} {d.family}</span><span className="text-sm text-primary">{t("use_this")} · {money(d.balance)}</span>
              </button>
            ))}
          </div>
        )}
        <BigButton onClick={save} className="w-full" data-testid="customer-save-btn"><Save className="w-5 h-5" />{confirmDup ? t("save_anyway") : t("save")}</BigButton>
      </DialogContent>
    </Dialog>
  );
}
